import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { Toast, useToast } from '../components/Toast';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

interface NoteItem {
  id: string;
  type: 'journal' | 'gift' | 'claims';
  content: string;
  date: string;
  author: string;
}

const INITIAL_NOTES: Record<string, NoteItem[]> = {
  'Nguyễn Văn Minh': [
    { id: '1', type: 'journal', content: 'Gặp gỡ khách hàng tại cafe Highlands, tư vấn gói Xanh Ước Mơ. Khách hàng khá quan tâm đến thẻ Sống Khỏe Mỗi Ngày nhưng chần chừ về mức phí. Hẹn tuần sau tư vấn tiếp.', date: '22/06/2026', author: 'Anh Minh (FA)' },
    { id: '2', type: 'gift', content: 'Gửi tặng lịch bàn Manulife và thiệp chúc mừng sinh nhật sớm cho khách hàng.', date: '15/06/2026', author: 'Anh Minh (FA)' },
  ],
  'Lê Văn Thành': [
    { id: '1', type: 'claims', content: 'Hỗ trợ khách hàng làm thủ tục giải quyết quyền lợi bảo hiểm (Claims) nằm viện 3 ngày do ngộ độc thực phẩm. Số tiền nhận hỗ trợ trợ cấp y tế: 1.500.000đ.', date: '10/06/2026', author: 'Anh Minh (FA)' },
  ],
};

const NOTE_TYPE_META = {
  journal: { label: 'Nhật ký tư vấn', icon: 'description', color: '#003366', bg: 'rgba(0, 51, 102, 0.08)' },
  gift: { label: 'Tặng quà chăm sóc', icon: 'card_giftcard', color: '#D97706', bg: 'rgba(217, 119, 6, 0.08)' },
  claims: { label: 'Hỗ trợ sự kiện bảo hiểm', icon: 'medical_services', color: '#E11D48', bg: 'rgba(225, 29, 72, 0.08)' },
};

export const M14NotesScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<RootStackParamList, 'M14NotesScreen'>>();
  const customerName = route.params?.customerName || 'Nguyễn Văn Minh';

  const [notes, setNotes] = useState<NoteItem[]>(INITIAL_NOTES[customerName] || [
    { id: 'd1', type: 'journal', content: 'Khởi tạo hồ sơ khách hàng eQuote. Bắt đầu theo dõi tiến trình tư vấn.', date: '24/06/2026', author: 'Anh Minh (FA)' }
  ]);
  const [newContent, setNewContent] = useState('');
  const [selectedType, setSelectedType] = useState<'journal' | 'gift' | 'claims'>('journal');
  const { toastProps, showToast, hideToast } = useToast();

  const handleAddNote = () => {
    if (!newContent.trim()) {
      showToast('Vui lòng nhập nội dung ghi chú', 'error');
      return;
    }

    const newNote: NoteItem = {
      id: Date.now().toString(),
      type: selectedType,
      content: newContent.trim(),
      date: new Date().toLocaleDateString('vi-VN'),
      author: 'Anh Minh (FA)',
    };

    setNotes([newNote, ...notes]);
    setNewContent('');
    showToast('Đã lưu ghi chú chăm sóc khách hàng', 'success');
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {/* Header Back & Context */}
        <View style={styles.headerContext}>
          <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
            <Icon name="arrow_back" size={20} color={colors.primary} />
            <Text style={styles.backBtnText}>Quay lại</Text>
          </TouchableOpacity>
          <Text style={styles.contextTitle}>Nhật ký CRM Chăm Sóc</Text>
          <Text style={styles.contextCustomer}>{customerName}</Text>
        </View>

        {/* Add Note Form */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Tạo ghi chú chăm sóc mới</Text>
          
          {/* Note Type Picker */}
          <Text style={styles.label}>Phân loại ghi chú:</Text>
          <View style={styles.typeSelectorRow}>
            {(['journal', 'gift', 'claims'] as const).map((t) => {
              const meta = NOTE_TYPE_META[t];
              const active = selectedType === t;
              return (
                <TouchableOpacity
                  key={t}
                  style={[
                    styles.typeBtn,
                    active && { borderColor: meta.color, backgroundColor: meta.bg }
                  ]}
                  onPress={() => setSelectedType(t)}
                >
                  <Icon name={meta.icon as any} size={18} color={active ? meta.color : '#64748b'} />
                  <Text style={[styles.typeBtnText, active && { color: meta.color, fontWeight: 'bold' }]}>
                    {meta.label.split(' ')[0]}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Text input */}
          <Text style={styles.label}>Nội dung chi tiết:</Text>
          <TextInput
            style={styles.textInput}
            multiline
            numberOfLines={4}
            value={newContent}
            onChangeText={setNewContent}
            placeholder="Nhập ghi chú phản hồi của khách hàng, quà đã tặng hoặc tình trạng giải quyết sự kiện bảo hiểm..."
          />

          <TouchableOpacity style={styles.submitBtn} onPress={handleAddNote}>
            <Icon name="add" size={18} color="#ffffff" />
            <Text style={styles.submitBtnText}>Thêm vào Nhật ký</Text>
          </TouchableOpacity>
        </View>

        {/* Notes Timeline List */}
        <Text style={styles.sectionHeaderTitle}>Lịch sử tương tác ({notes.length})</Text>
        
        {notes.length === 0 ? (
          <Text style={styles.emptyText}>Chưa có lịch sử chăm sóc nào.</Text>
        ) : (
          <View style={styles.timelineContainer}>
            {notes.map((note, index) => {
              const meta = NOTE_TYPE_META[note.type];
              return (
                <View key={note.id} style={styles.timelineItem}>
                  {/* Left node decoration */}
                  <View style={styles.timelineNodeCol}>
                    <View style={[styles.nodeCircle, { backgroundColor: meta.color }]}>
                      <Icon name={meta.icon as any} size={14} color="#ffffff" />
                    </View>
                    {index < notes.length - 1 && <View style={styles.nodeLine} />}
                  </View>

                  {/* Right Content card */}
                  <View style={styles.timelineContentCard}>
                    <View style={styles.noteHeader}>
                      <View style={[styles.typeBadge, { backgroundColor: meta.bg }]}>
                        <Text style={[styles.typeBadgeText, { color: meta.color }]}>{meta.label}</Text>
                      </View>
                      <Text style={styles.noteDate}>{note.date}</Text>
                    </View>
                    <Text style={styles.noteText}>{note.content}</Text>
                    <Text style={styles.noteAuthor}>Người viết: {note.author}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>

      {/* Toast — thay Alert.alert (no-op trên web) */}
      <Toast
        visible={toastProps.visible}
        message={toastProps.message}
        variant={toastProps.variant}
        onHide={hideToast}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  scrollContent: {
    padding: spacing[4],
    paddingBottom: 40,
    gap: spacing[4],
    maxWidth: 680,
    width: '100%',
    alignSelf: 'center',
  },
  headerContext: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
    marginBottom: spacing[2],
  },
  backBtnText: {
    fontSize: 13.5,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  contextTitle: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    textTransform: 'uppercase',
    fontWeight: fontWeights.semibold,
  },
  contextCustomer: {
    fontSize: 22,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginTop: 2,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[2.5],
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    paddingBottom: spacing[2],
  },
  label: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
    marginTop: spacing[1],
  },
  typeSelectorRow: {
    flexDirection: 'row',
    gap: spacing[2],
  },
  typeBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[1.5],
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    height: 40,
  },
  typeBtnText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  textInput: {
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[3],
    fontSize: 13.5,
    color: colors.onSurface,
    textAlignVertical: 'top',
    height: 90,
  },
  submitBtn: {
    backgroundColor: colors.primary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
    height: 44,
    borderRadius: borderRadius.DEFAULT,
    marginTop: spacing[1],
  },
  submitBtnText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    fontSize: 13.5,
  },

  sectionHeaderTitle: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginTop: spacing[2],
  },
  emptyText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: spacing[6],
  },

  /* TIMELINE TIMELINE */
  timelineContainer: {
    paddingLeft: spacing[2],
  },
  timelineItem: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  timelineNodeCol: {
    alignItems: 'center',
    width: 28,
  },
  nodeCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  nodeLine: {
    width: 2,
    flex: 1,
    backgroundColor: colors.outlineVariant,
    marginVertical: 4,
    zIndex: 1,
  },
  timelineContentCard: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    marginBottom: spacing[4],
    gap: spacing[1.5],
  },
  noteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  typeBadge: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: borderRadius.full,
  },
  typeBadgeText: {
    fontSize: 10,
    fontWeight: fontWeights.bold,
  },
  noteDate: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
  },
  noteText: {
    fontSize: 13,
    color: colors.onSurface,
    lineHeight: 18,
  },
  noteAuthor: {
    fontSize: 10.5,
    color: colors.onSurfaceVariant,
    fontStyle: 'italic',
    textAlign: 'right',
  },
});
